import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, AlertCircle, HelpCircle, ChevronDown } from 'lucide-react';
import { getDisplayName } from '../utils/displayName';

interface StrategizeMember {
  id: number;
  name: string;
  display_name?: string;
  email: string;
  gender?: 'male' | 'female' | null;
  is_captain: boolean;
  ntrp_rating?: number | null;
  dynamic_rating?: number | null;
  lineup_type_preferences?: string[] | null;
}

interface PlayerDropdownProps {
  value: number | null;
  onChange: (playerId: number | null) => void;
  eligiblePlayers: StrategizeMember[];
  ineligiblePlayers: Array<{ player: StrategizeMember; reason: string }>;
  matchId: number;
  getPlayerAvailability: (playerId: number, matchId: number) => string | null;
  getPlayerLabel: (player: StrategizeMember, matchId: number) => string;
  className?: string;
  disabled?: boolean;
  hasError?: boolean;
  isChanged?: boolean;
  isUnfilled?: boolean;
  matchType?: string;
}

const PlayerDropdown: React.FC<PlayerDropdownProps> = ({
  value,
  onChange,
  eligiblePlayers,
  ineligiblePlayers,
  matchId,
  getPlayerAvailability,
  getPlayerLabel,
  className = '',
  disabled = false,
  hasError = false,
  isChanged = false,
  isUnfilled = false,
  matchType,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [positionAbove, setPositionAbove] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top?: number; bottom?: number; left: number; width: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Get selected player
  const selectedPlayer = [...eligiblePlayers, ...ineligiblePlayers.map(p => p.player)].find(
    p => p.id === value
  );

  // Get availability icon component
  const getAvailabilityIcon = (availability: string | null) => {
    switch (availability) {
      case 'available':
        return <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />;
      case 'maybe':
        return <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0" />;
      case 'unavailable':
        return <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />;
      default:
        return <HelpCircle className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is outside both button and menu
      // This works for both portal (menu in body) and absolute (menu in container) cases
      const clickedOutsideButton = buttonRef.current && !buttonRef.current.contains(target);
      const clickedOutsideMenu = menuRef.current && !menuRef.current.contains(target);
      
      // Close if clicked outside both button and menu
      if (clickedOutsideButton && clickedOutsideMenu) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const selectablePlayers = eligiblePlayers;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex(prev => {
            // -1 is "Select Player", 0+ are actual players
            const maxIndex = selectablePlayers.length - 1;
            const next = prev < maxIndex ? prev + 1 : prev;
            return next;
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex(prev => {
            // -1 is "Select Player", 0+ are actual players
            const next = prev >= 0 ? prev - 1 : -1;
            return next;
          });
          break;
        case 'Enter':
          event.preventDefault();
          if (highlightedIndex === -1) {
            // "Select Player" option
            onChange(null);
            setIsOpen(false);
            setHighlightedIndex(-1);
          } else if (highlightedIndex >= 0 && highlightedIndex < selectablePlayers.length) {
            onChange(selectablePlayers[highlightedIndex].id);
            setIsOpen(false);
            setHighlightedIndex(-1);
          }
          break;
        case 'Escape':
          event.preventDefault();
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, highlightedIndex, eligiblePlayers, ineligiblePlayers, onChange]);

  // Reset highlighted index when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(-1);
    } else {
      setPositionAbove(false);
      setMenuPosition(null);
    }
  }, [isOpen]);

  // Update dropdown position on scroll when using portal
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const updatePosition = () => {
      if (buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const menuHeight = 320; // max-h-80 is ~320px
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        
        // Determine if should position above
        const shouldPositionAbove = spaceBelow < menuHeight + 8 && spaceAbove > spaceBelow;
        setPositionAbove(shouldPositionAbove);
        
        // Update menu position
        setMenuPosition({
          top: shouldPositionAbove ? undefined : buttonRect.bottom + 4,
          bottom: shouldPositionAbove ? window.innerHeight - buttonRect.top + 4 : undefined,
          left: buttonRect.left,
          width: buttonRect.width,
        });
      }
    };

    // Initial position
    updatePosition();

    // Update on scroll (both window and element scroll)
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    // Also listen to scroll on all parent elements
    let element: HTMLElement | null = buttonRef.current;
    const scrollElements: HTMLElement[] = [];
    while (element && element !== document.body) {
      if (element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth) {
        scrollElements.push(element);
        element.addEventListener('scroll', updatePosition, true);
      }
      element = element.parentElement;
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      scrollElements.forEach(el => el.removeEventListener('scroll', updatePosition, true));
    };
  }, [isOpen]);

  // Calculate dropdown position based on available space
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (buttonRef.current && menuRef.current) {
          const buttonRect = buttonRef.current.getBoundingClientRect();
          const menuHeight = menuRef.current.offsetHeight || 320; // max-h-80 is ~320px
          const spaceBelow = window.innerHeight - buttonRect.bottom;
          const spaceAbove = buttonRect.top;
          
          // Position above if there's not enough space below but enough space above
          // Add a small buffer (8px) for margins
          if (spaceBelow < menuHeight + 8 && spaceAbove > spaceBelow) {
            setPositionAbove(true);
          } else {
            setPositionAbove(false);
          }
        }
      });
    }
  }, [isOpen, eligiblePlayers.length, ineligiblePlayers.length]);

  const handlePlayerSelect = (playerId: number) => {
    onChange(playerId === 0 ? null : playerId);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // Sort ineligible players alphabetically
  const sortedIneligiblePlayers = [...ineligiblePlayers].sort((a, b) =>
    getDisplayName(a.player).localeCompare(getDisplayName(b.player))
  );

  // Determine button styling - use className for text size, default to text-xs
  const isSmallText = className?.includes('text-sm') ?? false;
  const textSizeClass = isSmallText ? 'text-sm px-2 py-1' : 'text-xs px-1 py-0.5';
  const focusRingClass = isSmallText ? 'focus:ring-2' : 'focus:ring-1';
  const buttonClassName = `w-full ${textSizeClass} border rounded focus:outline-none ${focusRingClass} bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 flex items-center justify-between ${
    hasError
      ? 'border-red-500 dark:border-red-400 focus:ring-red-500 bg-red-50 dark:bg-red-900/30'
      : isChanged
      ? 'border-gray-300 dark:border-gray-600 focus:ring-blue-500 bg-yellow-100 dark:bg-yellow-900/30'
      : isUnfilled
      ? 'border-gray-300 dark:border-gray-600 focus:ring-blue-500 bg-red-50 dark:bg-red-900/30'
      : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
  } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`;

  const displayText = selectedPlayer
    ? getPlayerLabel(selectedPlayer, matchId).replace(/^[🟢🟡🔴❓]\s*/, '') // Remove emoji if present
    : 'Select Player';
  
  const selectedPlayerAvailability = selectedPlayer
    ? getPlayerAvailability(selectedPlayer.id, matchId)
    : null;
  
  // Always show availability icon for selected player (including null/unset)
  const shouldShowAvailabilityIcon = selectedPlayer !== undefined;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={buttonClassName}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <div className="flex items-center space-x-1.5 flex-1 min-w-0">
          {shouldShowAvailabilityIcon && (
            <div className="flex-shrink-0">
              {getAvailabilityIcon(selectedPlayerAvailability)}
            </div>
          )}
          <span className="truncate text-left">{displayText}</span>
        </div>
        <ChevronDown
          className={`w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0 ml-1.5 transition-transform ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (() => {
        const renderMenuContent = () => (
          <>
            {/* Eligible Players Section */}
            {eligiblePlayers.length > 0 && (
              <div className="py-0.5">
                {eligiblePlayers.map((player, index) => {
                  const availability = getPlayerAvailability(player.id, matchId);
                  const isHighlighted = index === highlightedIndex;
                  const isSelected = player.id === value;
                  const itemTextSize = (className?.includes('text-sm') ?? false) ? 'text-sm' : 'text-xs';

                  return (
                    <button
                      key={player.id}
                      type="button"
                      onClick={() => handlePlayerSelect(player.id)}
                      className={`w-full px-2 py-1 text-left ${itemTextSize} flex items-center space-x-1.5 transition-colors ${
                        isHighlighted || isSelected
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
                          : 'text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      {getAvailabilityIcon(availability)}
                      <span className="flex-1 whitespace-nowrap">
                        {getDisplayName(player)}
                        {(player.ntrp_rating !== null && player.ntrp_rating !== undefined) || (player.dynamic_rating !== null && player.dynamic_rating !== undefined) ? (
                          <span className="text-gray-500 dark:text-gray-400 ml-1">
                            ({player.ntrp_rating !== null && player.ntrp_rating !== undefined ? player.ntrp_rating.toFixed(1) : '–'}
                            {player.dynamic_rating !== null && player.dynamic_rating !== undefined && (
                              <span className="text-purple-500 dark:text-purple-400">/{player.dynamic_rating.toFixed(2)}</span>
                            )}
                            )
                          </span>
                        ) : null}
                      </span>
                      {matchType && player.lineup_type_preferences && player.lineup_type_preferences.length > 0 && (() => {
                        const prefIndex = player.lineup_type_preferences.indexOf(matchType);
                        if (prefIndex === -1) return null;
                        return (
                          <span
                            className={`flex-shrink-0 text-[10px] font-medium rounded px-1 ${
                              prefIndex === 0
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                                : 'bg-gray-100 text-gray-500 dark:bg-gray-600 dark:text-gray-400'
                            }`}
                            title={prefIndex === 0 ? '1st choice' : `Choice #${prefIndex + 1}`}
                          >
                            {prefIndex === 0 ? '★' : `#${prefIndex + 1}`}
                          </span>
                        );
                      })()}
                      {isSelected && (
                        <span className="text-blue-600 dark:text-blue-400 text-xs">✓</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Separator and Ineligible Players Section */}
            {sortedIneligiblePlayers.length > 0 && (
              <>
                <div className="border-t border-gray-200 dark:border-gray-700 my-0.5"></div>
                <div className="bg-gray-50 dark:bg-gray-900/50">
                  <div className={`px-2 py-1 ${(className?.includes('text-sm') ?? false) ? 'text-sm' : 'text-xs'} font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700`}>
                    Not Eligible
                  </div>
                  {sortedIneligiblePlayers.map(({ player, reason }) => {
                    const availability = getPlayerAvailability(player.id, matchId);
                    const itemTextSize = (className?.includes('text-sm') ?? false) ? 'text-sm' : 'text-xs';

                    return (
                      <div
                        key={player.id}
                        className={`px-2 py-1 ${itemTextSize} flex items-center space-x-1.5 text-gray-500 dark:text-gray-400 cursor-not-allowed`}
                        title={reason}
                      >
                        <div className="opacity-50">
                          {getAvailabilityIcon(availability)}
                        </div>
                        <span className="flex-1 whitespace-nowrap">
                          {getDisplayName(player)}
                          {(player.ntrp_rating !== null && player.ntrp_rating !== undefined) || (player.dynamic_rating !== null && player.dynamic_rating !== undefined) ? (
                            <span className="text-gray-400 dark:text-gray-500 ml-1">
                              ({player.ntrp_rating !== null && player.ntrp_rating !== undefined ? player.ntrp_rating.toFixed(1) : '–'}
                              {player.dynamic_rating !== null && player.dynamic_rating !== undefined && (
                                <span className="text-purple-400 dark:text-purple-500">/{player.dynamic_rating.toFixed(2)}</span>
                              )}
                              )
                            </span>
                          ) : null}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {eligiblePlayers.length === 0 && sortedIneligiblePlayers.length === 0 && (
              <div className={`px-2 py-1 ${(className?.includes('text-sm') ?? false) ? 'text-sm' : 'text-xs'} text-gray-500 dark:text-gray-400 text-center flex items-center justify-center`}>
                No players available
              </div>
            )}

            {/* Clear/Unset Option - at bottom */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-0.5 pb-0.5 px-1">
              <button
                type="button"
                onClick={() => handlePlayerSelect(0)}
                className={`w-full px-1.5 py-0.5 text-center ${(className?.includes('text-sm') ?? false) ? 'text-sm' : 'text-xs'} font-medium rounded transition-colors ${
                  !value || value === 0
                    ? 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
                onMouseEnter={() => setHighlightedIndex(-1)}
              >
                Clear
              </button>
            </div>
          </>
        );

        // Use portal when near bottom of viewport, in scrollable container, or on mobile to escape overflow constraints
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
        const shouldUsePortal = isMobile || (buttonRef.current && (() => {
          const buttonRect = buttonRef.current!.getBoundingClientRect();
          const menuHeight = 320; // max-h-80 is ~320px
          const spaceBelow = window.innerHeight - buttonRect.bottom;
          const spaceAbove = buttonRect.top;
          
          // Check if we're inside a table (common case for clipping)
          let parent = buttonRef.current.parentElement;
          let inTable = false;
          let inScrollableContainer = false;
          
          while (parent && parent !== document.body) {
            const tagName = parent.tagName?.toLowerCase();
            if (tagName === 'table' || tagName === 'td' || tagName === 'th') {
              inTable = true;
            }
            
            const style = window.getComputedStyle(parent);
            const overflow = style.overflow + style.overflowX + style.overflowY;
            if (overflow.includes('auto') || overflow.includes('hidden') || overflow.includes('scroll')) {
              // Check if this container would clip the dropdown
              const parentRect = parent.getBoundingClientRect();
              const wouldClipBelow = buttonRect.bottom + menuHeight > parentRect.bottom - 5; // 5px buffer
              const wouldClipAbove = positionAbove && buttonRect.top - menuHeight < parentRect.top + 5;
              if (wouldClipBelow || wouldClipAbove) {
                inScrollableContainer = true;
                break;
              }
            }
            parent = parent.parentElement;
          }
          
          // Use portal if:
          // 1. Inside a table (tables often have overflow constraints)
          // 2. Not enough space below (less than menu height + buffer)
          // 3. In a scrollable container that would clip the dropdown
          // 4. Not enough space above when positioning above
          return inTable || 
                 spaceBelow < menuHeight + 20 || 
                 inScrollableContainer || 
                 (positionAbove && spaceAbove < menuHeight + 20);
        })());
        
        if (shouldUsePortal && buttonRef.current) {
          // Use calculated position if available, otherwise calculate on the fly
          const buttonRect = buttonRef.current.getBoundingClientRect();
          const currentPosition = menuPosition || {
            top: positionAbove ? undefined : buttonRect.bottom + 4,
            bottom: positionAbove ? window.innerHeight - buttonRect.top + 4 : undefined,
            left: buttonRect.left,
            width: buttonRect.width,
          };
          
          const menuStyle: React.CSSProperties = {
            position: 'fixed',
            top: currentPosition.top !== undefined ? `${currentPosition.top}px` : 'auto',
            bottom: currentPosition.bottom !== undefined ? `${currentPosition.bottom}px` : 'auto',
            left: `${currentPosition.left}px`,
            minWidth: `${currentPosition.width}px`,
            width: 'auto',
            zIndex: 9999,
          };

          return createPortal(
            <div 
              ref={menuRef}
              className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg shadow-xl border border-white/30 dark:border-gray-700/30 max-h-80 overflow-auto"
              style={menuStyle}
            >
              {renderMenuContent()}
            </div>,
            document.body
          );
        }
        
        return (
          <div 
            ref={menuRef}
            className={`absolute z-50 w-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg shadow-xl border border-white/30 dark:border-gray-700/30 max-h-80 overflow-auto ${
              positionAbove ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}
          >
            {renderMenuContent()}
          </div>
        );
      })()}
    </div>
  );
};

export default PlayerDropdown;
